/**
 * @file `SignInPopover` — anchored sign-in popover for the topbar
 *       "Sign in" button.
 * @module @labcraft/ui/chrome/sign-in/sign-in-popover
 *
 * Controlled API: caller renders the trigger (e.g., the Topbar's
 * "Sign in" pill), passes `anchorEl` + `open` + `onClose`. Popover
 * positions itself fixed below the anchor's right edge, no portal
 * (renders inline in the DOM so React events bubble naturally).
 *
 * Close affordances:
 * - Click outside the popover (anchor click is detected separately
 *   so the trigger can toggle the popover open/closed).
 * - Escape key.
 * - The wrapped {@link SignInCard} stays open after a successful
 *   email submit and shows its "Check your inbox" panel in-place —
 *   the user must click outside (or press Escape) to dismiss.
 *
 * Cross-domain note: after a successful sign-in, the session cookie
 * is `Domain=.labcraft.dev` (see HANDOFF.md), so every subdomain
 * (`www`, `blog`, `lab`) sees the user immediately and renders
 * {@link AccountMenu} instead of this popover.
 */

import { useEffect, useLayoutEffect, useRef, useState, type JSX } from 'react'
import { SignInCard, type SignInCardProps } from './sign_in_card.tsx'
import styles from './sign_in_popover.module.css'

/**
 * Props for {@link SignInPopover}.
 *
 * Extends a subset of {@link SignInCardProps} — `next` and the
 * three flow callbacks. `expired` is intentionally omitted: the
 * popover is opened by an explicit user click, not by a session
 * timeout, so the "your session timed out" notice only makes sense
 * on the standalone `/authorize` route.
 *
 * @property anchorEl - The DOM node the popover anchors to. Pass a
 *   ref's `.current`. Pass `null` when the trigger hasn't mounted
 *   yet; the popover stays unmounted.
 * @property open - Whether the popover is shown. The caller owns
 *   this state so the trigger can render its own
 *   `aria-expanded={open}` etc.
 * @property onClose - Invoked on outside-click or Escape. Caller
 *   sets `open={false}`.
 * @property placement - Default `'bottom-end'`. Controls horizontal
 *   alignment of the popover relative to the anchor. Top-edge
 *   alignment is always `anchor.bottom + offset`.
 */
export interface SignInPopoverProps extends Pick<
  SignInCardProps,
  | 'next'
  | 'onContinueWithGithub'
  | 'onContinueWithGoogle'
  | 'onSubmitEmail'
  | 'onEmailSent'
  | 'title'
  | 'lede'
  | 'fineprint'
> {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  placement?: 'bottom-end' | 'bottom-start' | 'bottom-center'
}

/** Pixels between the anchor's bottom edge and the popover's top. */
const ANCHOR_OFFSET = 10

export function SignInPopover(props: SignInPopoverProps): JSX.Element | null {
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const [position, setPosition] = useState<{
    top: number
    left: number
    arrowLeft: number
  } | null>(null)

  // Recompute position when the popover opens or the viewport changes.
  useLayoutEffect(() => {
    if (!props.open || !props.anchorEl) {
      setPosition(null)
      return
    }
    const compute = (): void => {
      const anchor = props.anchorEl
      if (!anchor) return
      const aRect = anchor.getBoundingClientRect()
      const popover = popoverRef.current
      const pWidth = popover ? popover.offsetWidth : 340
      const placement = props.placement ?? 'bottom-end'
      let left: number
      if (placement === 'bottom-start') {
        left = aRect.left
      } else if (placement === 'bottom-center') {
        left = aRect.left + aRect.width / 2 - pWidth / 2
      } else {
        left = aRect.right - pWidth
      }
      // Clamp to viewport with an 8px margin so a far-right anchor
      // doesn't push the popover off-screen.
      const margin = 8
      left = Math.max(
        margin,
        Math.min(left, window.innerWidth - pWidth - margin),
      )
      const top = aRect.bottom + ANCHOR_OFFSET
      // Arrow points back at the anchor's horizontal centre.
      const anchorCenter = aRect.left + aRect.width / 2
      const arrowLeft = Math.max(16, Math.min(anchorCenter - left, pWidth - 24))
      setPosition({ top, left, arrowLeft })
    }
    compute()
    window.addEventListener('resize', compute)
    window.addEventListener('scroll', compute, true)
    return () => {
      window.removeEventListener('resize', compute)
      window.removeEventListener('scroll', compute, true)
    }
  }, [props.open, props.anchorEl, props.placement])

  // Outside click + Escape close. We exclude the anchor itself from
  // outside-click so the trigger button can toggle the popover (a
  // second click on the trigger while open should close it; the
  // caller already flips `open` on trigger click, and we'd be
  // racing it if we also called `onClose` here).
  useEffect(() => {
    if (!props.open) return
    const onDocClick = (e: MouseEvent): void => {
      const target = e.target as Node
      if (popoverRef.current?.contains(target)) return
      if (props.anchorEl?.contains(target)) return
      props.onClose()
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') props.onClose()
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [props.open, props.anchorEl, props.onClose])

  if (!props.open) return null

  return (
    <div
      ref={popoverRef}
      className={styles.popover}
      role="dialog"
      aria-label="Sign in"
      style={
        position
          ? { top: position.top, left: position.left }
          : { visibility: 'hidden' }
      }>
      <span
        className={styles.arrow}
        style={position ? { left: position.arrowLeft } : undefined}
        aria-hidden
      />
      <SignInCard
        variant="embedded"
        title={props.title ?? 'Sign in'}
        lede={
          props.lede === undefined
            ? 'Same buttons sign up new accounts and sign in returning ones.'
            : props.lede
        }
        fineprint={props.fineprint === undefined ? null : props.fineprint}
        next={props.next ?? null}
        onContinueWithGithub={props.onContinueWithGithub}
        onContinueWithGoogle={props.onContinueWithGoogle}
        onSubmitEmail={props.onSubmitEmail}
        {...(props.onEmailSent && { onEmailSent: props.onEmailSent })}
      />
    </div>
  )
}
